/**
 * Default navigation configuration for the new design.
 *
 * Organized by case lifecycle stages instead of arbitrary groups.
 * Maps to the existing route structure.
 *
 * Import your MUI icons for each item, or provide custom icons.
 */

import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import GradingIcon from '@mui/icons-material/Grading';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BalanceIcon from '@mui/icons-material/Balance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableChartIcon from '@mui/icons-material/TableChart';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryIcon from '@mui/icons-material/Category';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SyncIcon from '@mui/icons-material/Sync';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContactsIcon from '@mui/icons-material/Contacts';
import SchoolIcon from '@mui/icons-material/School';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

/**
 * Navigation sections organized by lifecycle stage.
 * Each section has a label and array of items.
 *
 * Items support:
 *   path: string — route path
 *   displayName: string — visible label
 *   icon: React component — MUI icon
 *   badge: string | number — attention indicator (optional)
 *   caseRequired: boolean — requires active case
 *   orgRequired: boolean — requires active org
 *   transactionsRequired: boolean — requires transactions
 *   customerAdminRequired: boolean — requires userProfile.isCustomerAdmin()
 *   hidden: boolean — dynamically hide item
 *   component: string — page component name (for route matching)
 */
const defaultNavigationSections = [
  {
    label: null, // No header for top-level
    items: [
      {
        path: '/',
        displayName: 'Dashboard',
        icon: DashboardIcon,
        component: 'HomePage',
      },
    ],
  },
  {
    label: 'Case Setup',
    items: [
      {
        path: '/case_maintenance_sheet',
        displayName: 'Case Details',
        icon: DescriptionIcon,
        component: 'CaseMaintenancePage',
        caseRequired: true,
      },
      {
        path: '/manage_entities',
        displayName: 'Entities & Ownership',
        icon: AccountTreeIcon,
        component: 'EntitiesPage',
        caseRequired: true,
      },
      {
        path: '/manage_uploaded_files',
        displayName: 'Upload Files',
        icon: UploadFileIcon,
        component: 'ManageUploadedFiles',
        caseRequired: true,
      },
    ],
  },
  {
    label: 'Review',
    items: [
      {
        path: '/review_statement_quality',
        displayName: 'Quality & Completeness',
        icon: CheckCircleIcon,
        component: 'StatementQualityReportPage',
        caseRequired: true,
        transactionsRequired: true,
      },
      {
        path: '/view_categories',
        displayName: 'Categories',
        icon: CategoryIcon,
        component: 'ViewCategories',
        caseRequired: true,
        transactionsRequired: true,
      },
    ],
  },
  {
    label: 'Analysis',
    items: [
      {
        path: '/accounts_net_worth',
        displayName: 'Accounts & Net Worth',
        icon: ShowChartIcon,
        component: 'AccountsNetWorthPage',
        caseRequired: true,
        transactionsRequired: true,
      },
      {
        path: '/report_sankey_analysis/CATEGORY',
        displayName: 'Transaction Analysis',
        icon: AccountTreeIcon,
        component: 'SankeyAnalysisPage',
        caseRequired: true,
        transactionsRequired: true,
      },
      // "Transfer Analysis" (perspective=INTERNAL_TRANSFER) retired with the
      // Sankey Refresh — transfers are excluded from the flow-of-funds Sankey
      // and get a dedicated Chord view later (Trello FkCeGYdu).
    ],
  },
  {
    label: 'Reports',
    items: [
      {
        path: '/report_drfa_sheet',
        displayName: 'Financial Affidavit',
        icon: BalanceIcon,
        component: 'DRFAReportPage',
        caseRequired: true,
      },
      {
        path: '/report_marital_balance_sheet',
        displayName: 'Marital Balance Sheet (MBS)',
        icon: StorefrontIcon,
        component: 'MaritalBalanceSheetReportPage',
        caseRequired: true,
      },
      {
        path: '/report_estate_summary_export',
        displayName: 'Estate Summary Export',
        icon: TableChartIcon,
        component: 'EstateSummaryExportPage',
        caseRequired: true,
      },
      {
        path: '/livi_files',
        displayName: 'Livi Files',
        icon: FolderOpenIcon,
        component: 'LiviFilesPage',
        caseRequired: true,
      },
      {
        path: '/manage_accounts',
        displayName: 'Manage MBS Accounts',
        icon: AccountBalanceIcon,
        component: 'AccountManagementPage',
        caseRequired: true,
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        path: '/admin',
        displayName: 'Admin Console',
        icon: SettingsIcon,
        component: 'AdminPage',
      },
      {
        path: '/case_costs',
        displayName: 'Case Costs (internal)',
        icon: AccountBalanceIcon,
        component: 'CaseCostsPage',
        caseRequired: true,
      },
      {
        path: '/system_costs',
        displayName: 'System Costs (internal)',
        icon: AccountBalanceIcon,
        component: 'SystemCostsPage',
      },
      {
        path: '/rate_card_editor',
        displayName: 'AI Rate Card (internal)',
        icon: AccountBalanceIcon,
        component: 'RateCardEditorPage',
      },
      {
        path: '/lesson_review',
        displayName: 'Livi Lessons (internal)',
        icon: SchoolIcon,
        component: 'LessonReviewPage',
      },
      {
        path: '/setup_account',
        displayName: 'Set Up Account',
        icon: PersonAddIcon,
        component: 'SetupAccountPage',
      },
      {
        path: '/manage_org_user',
        displayName: 'Team',
        icon: PeopleIcon,
        component: 'ManageOrgUserPage',
        orgRequired: true,
      },
      {
        path: '/manage_customers',
        displayName: 'Customers',
        icon: BusinessIcon,
        component: 'ManageCustomersPage',
      },
      {
        path: '/prospects',
        displayName: 'Prospects',
        icon: ContactsIcon,
        component: 'ProspectsPage',
      },
      {
        path: '/manage_customer_cases',
        displayName: 'Cases',
        icon: GavelIcon,
        component: 'ManageCustomerCasesPage',
        customerAdminRequired: true,
      },
      {
        path: '/billing_settings',
        displayName: 'Billing Settings',
        icon: RequestPageIcon,
        component: 'BillingSettingsPage',
        customerAdminRequired: true,
      },
      {
        path: '/manage_case_quotas',
        displayName: 'Case Quotas',
        icon: GavelIcon,
        component: 'QuotaManagementPage',
        caseRequired: true,
      },
      {
        path: '/manage_case_assignment',
        displayName: 'Assignments',
        icon: AssignmentIndIcon,
        component: 'ManageCaseAssignmentPage',
        caseRequired: true,
      },
      {
        path: '/manage_org_group_assignment',
        displayName: 'Org Group Assignment',
        icon: AdminPanelSettingsIcon,
        component: 'ManageOrgGroupAssignmentPage',
        orgRequired: true,
      },
      {
        path: '/manage_case_group_assignment',
        displayName: 'Case Group Assignment',
        icon: AdminPanelSettingsIcon,
        component: 'ManageCaseGroupAssignmentPage',
        caseRequired: true,
      },
      {
        path: '/view_categories',
        displayName: 'View Categories',
        icon: WorkspacesIcon,
        component: 'ViewCategories',
        caseRequired: true,
      },
      {
        path: '/emergency_dump',
        displayName: 'Emergency Case Dump',
        icon: CloudDownloadIcon,
        component: 'EmergencyDump',
        caseRequired: true,
        transactionsRequired: true,
      },
      {
        path: '/migrate_case_database',
        displayName: 'Migrate Case Database',
        icon: SyncIcon,
        component: 'MigrateCaseDatabasePage',
      },
      {
        path: '/manage_superadmin_user',
        displayName: 'Global Admins',
        icon: PeopleIcon,
        component: 'ManageUserPage',
      },
    ],
  },
];

export default defaultNavigationSections;
